import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useShuffleArenaOfferMutation } from '@/api/queries/shop';
import { playSound } from '@/common/lib/soundEffect';
import { type ArenaOffersDto } from '@/common/types/shop/marketBoxes';
import { ModalContext } from '@/components/Modals/Modal/config';
import {
  Glow,
  ShopBadge,
  ShopBuyButton,
  ShuffleButton,
} from '@/components/Shop/ui/ShopUi';
import { Picture } from '@/components/ui/Picture/Picture';

import { arenaOfferColors, arenaOffersInfo } from './config';
import RewardArray from './RewardArray';

import css from './style.module.scss';

const formatTimer = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return `${String(hours).padStart(2, '0')}h : ${String(minutes).padStart(2, '0')}m`;
};

const ArenaOfferCard: React.FC<ArenaOffersDto> = (arenaOffer) => {
  const { id, name, reward, cost, discountTimeLeftInSeconds } = arenaOffer;
  const [shuffle, { isLoading }] = useShuffleArenaOfferMutation();
  const { openModal } = useContext(ModalContext);
  const { t } = useTranslation();
  const info = arenaOffersInfo[name];
  const colors = arenaOfferColors[info.color ?? 'purple'];

  const isDiscount: boolean = cost.has_discount;
  const prices = isDiscount ? cost.discounted : cost.base;
  const isSuperStart = name === 'superStart';
  // Weekend kits can't be shuffled ("LIMITED" cards)
  const isWeekend = [
    'growthKitSmall',
    'growthKitBig',
    'growthKitGiant',
  ].includes(name);

  const onBuyOffer = (): void => {
    openModal({
      renderKind: 'buyArenaOffer',
      props: { arenaOffer },
    });
  };

  const timer = isDiscount
    ? isSuperStart
      ? t('for 1-6 lvl')
      : formatTimer(discountTimeLeftInSeconds)
    : undefined;

  const buyButton = (
    <ShopBuyButton
      usd={prices.usd}
      oldUsd={isDiscount && !isSuperStart ? cost.base.usd : undefined}
      discount={
        isDiscount && !isSuperStart ? cost.displayDiscountPct : undefined
      }
      timer={timer}
      wrapperClassName={isWeekend ? 'w-[178px] max-w-[48%]' : 'flex-1 min-w-0'}
      onClick={onBuyOffer}
    />
  );

  return (
    <div className={css.offerCard}>
      <div className={css.bg}>
        <Picture
          img={info.img.png}
          webp={info.img.webp}
          className={css.art}
          alt=""
        />
        <Glow
          color={colors.glow}
          className="opacity-50 size-40 -left-20 -top-20"
        />
        <Glow
          color={colors.glow}
          className="opacity-20 size-40 left-1/2 -translate-x-1/2 -bottom-16"
        />
      </div>

      <ShopBadge
        text={t(isDiscount ? info.ticker.discount : info.ticker.default)}
        color={info.color ?? 'purple'}
      />

      <div className="relative flex flex-col gap-0.5">
        <h3 className={css.title}>{t(info.title)}</h3>
        <p className={css.describe} style={{ color: colors.text }}>
          {t(info.describe)}
        </p>
      </div>

      <RewardArray
        variant="medium"
        name={name}
        reward={reward}
        offerId={id}
        classNames="relative"
      />

      <div
        className={cn(
          'relative flex items-center gap-2 w-full',
          isWeekend && 'mt-6',
        )}
      >
        {!isWeekend && (
          <ShuffleButton
            className="flex-1 min-w-0"
            isLoading={isLoading}
            onClick={() => {
              playSound('buttons/shuffle');
              shuffle({ id, body: reward });
            }}
          />
        )}
        {buyButton}
      </div>
    </div>
  );
};

export default ArenaOfferCard;
